import React, { type FC, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { colors } from '@/theme/colors';

import { getSpacing, type SpacingToken } from '@/theme/spacing';

type DividerProps = {
  /** 'horizontal' = horizontal line (full width), 'vertical' = vertical line (full height) */
  orientation?: 'horizontal' | 'vertical';
  /** Spacing before/after (margin). Token or number. */
  spacing?: SpacingToken | number;
  /** Line thickness (default 1) */
  thickness?: number;
  /** Color (default border) */
  color?: string;
};

/**
 * Divider – horizontal or vertical line. Use spacing so it does not sit flush against content.
 */
export const Divider: FC<DividerProps> = ({
  orientation = 'horizontal',
  spacing,
  thickness = 1,
  color = colors['5'],
}) => {
  const margin = spacing !== undefined ? getSpacing(spacing) : 0;
  const isHorizontal = orientation === 'horizontal';
  const orientationStyle = isHorizontal ? styles.horizontal : styles.vertical;
  const sizeAndSpacingStyle = useMemo(
    () =>
      isHorizontal
        ? { height: thickness, marginVertical: margin }
        : { width: thickness, marginHorizontal: margin },
    [isHorizontal, margin, thickness],
  );
  const colorStyle = useMemo(() => ({ backgroundColor: color }), [color]);

  return <View style={[orientationStyle, sizeAndSpacingStyle, colorStyle]} />;
};

const styles = StyleSheet.create({
  horizontal: {
    width: '100%',
  },
  vertical: {
    height: '100%',
  },
});
